/**
 * RimLanding — IRA Specialist landing.
 *
 * Two entry points (per gap G-RIM-01):
 *   1. Customer search → shared `GlobalCustomerSearch` filtered to AcctType=60
 *      (IRA). Clicking a result routes to /rcm/:cifId so the IRA Specialist
 *      can see the full IRA list on the customer's Products tab and drill in.
 *      Follow-on wave: port the multi-IRA chip picker directly here.
 *   2. Direct plan-number entry (legacy fast path) — enter the 10-digit Field 802
 *      and route to /rim/:planNumber. For specialists who already know the plan.
 *
 * Access gate: the `IRA` permission is enforced at the Function; UI just
 * surfaces a friendly message for officers without the role.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, EntityIcon, GlobalCustomerSearch, Pill } from '@/components/primitives';
import type { CustomerSearchRow } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { IRA_PLAN_TYPES } from './constants';

export function RimLanding(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useConfig();
  const [planNumber, setPlanNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const hasIra = user?.roles?.includes?.('Supervisor')
    || (config.role?.permissions?.['IraAccess'] === true)
    || (config.role?.permissions?.['IRA'] === true);

  const open = (): void => {
    const clean = planNumber.trim();
    if (clean.length !== 10 || !/^\d{10}$/.test(clean)) {
      setError('Plan number must be exactly 10 digits.');
      return;
    }
    setError(null);
    navigate(`/rim/${clean}`);
  };

  // Customer-search hand-off: IRA list lives on the customer's Products tab in
  // RCM. Follow-on wave will add an inline IRA picker here so the specialist
  // never leaves RIM.
  const handleCustomerSelect = (row: CustomerSearchRow): void => {
    navigate(`/rcm/${row.cifId}`);
  };

  if (!hasIra) {
    return (
      <div className="rim-landing">
        <header className="rim-landing__header">
          <h1>IRA Management</h1>
          <div className="rim-landing__sub">IRA Specialist permission required.</div>
        </header>
        <EmptyState
          title="IRA permission not assigned"
          message="Talk to your supervisor or compliance lead to add the IRA role to your user profile."
        />
      </div>
    );
  }

  return (
    <div className="rim-landing">
      <header className="rim-landing__header">
        <h1>IRA Management</h1>
        <div className="rim-landing__sub">
          Traditional · Roth · SEP · SIMPLE · Coverdell · Inherited.  Plan numbers are 10 digits;
          the first five identify the plan type.  <Pill variant="info">{user?.primaryRole ?? 'Officer'}</Pill>
        </div>
      </header>

      <section aria-label="Find customer with an IRA">
        <h2
          style={{
            fontSize: 14,
            color: 'var(--pcb-blue-dark)',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            marginBottom: 'var(--sp-4)',
          }}
        >
          Find customer with an IRA
        </h2>
        <GlobalCustomerSearch
          filterAcctType="60"
          acctTypeLabel="IRA"
          onSelect={handleCustomerSelect}
          placeholder="SSN, name, or CIF of the IRA owner"
        />
      </section>

      <div className="rim-plan-card" style={{ cursor: 'default' }}>
        <div>
          <h2 className="rim-plan-title">Open a plan by number</h2>
          <div className="rim-plan-meta">
            Enter the 10-digit plan number (Field 802).  Fast path for IRA Specialists
            who already know the plan.
          </div>
          <div style={{ marginTop: 'var(--sp-6)', display: 'flex', gap: 'var(--sp-4)', maxWidth: 400 }}>
            <input
              className="rim-benef-field"
              placeholder="0000000000"
              value={planNumber}
              onChange={(e) => setPlanNumber(e.currentTarget.value)}
              inputMode="numeric"
              maxLength={10}
            />
            <Button variant="primary" onClick={open}>Open plan</Button>
          </div>
          {error && (
            <div role="alert" style={{ color: 'var(--pcb-red)', fontSize: 12, marginTop: 'var(--sp-3)' }}>{error}</div>
          )}
        </div>
        <EntityIcon type="ira" code="IR" size={48} />
      </div>

      <section>
        <h2 style={{ fontSize: 14, color: 'var(--pcb-blue-dark)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Plan-type reference
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--sp-5)', marginTop: 'var(--sp-5)' }}>
          {IRA_PLAN_TYPES.map((t) => (
            <div
              key={t.code}
              style={{
                padding: 'var(--sp-6)',
                background: 'var(--surface-card)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--r-sm)',
                fontSize: 12,
              }}
            >
              <div style={{ fontWeight: 700, color: 'var(--pcb-blue-dark)' }}>{t.label}</div>
              <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-600)', marginTop: 4 }}>
                {t.prefix}XXXXX · {t.code}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="rim-rmd-banner" role="note">
        <strong>Reminder:</strong> IRA beneficiaries are captured via
        <code style={{ fontFamily: 'var(--font-mono)', margin: '0 4px' }}>Customer/TaxPlnBenfAdd</code>
        — NOT CustRelAdd.  Don&apos;t cross wires.
      </div>
    </div>
  );
}
